import { makeDemoVariants } from "./demo";
import type { DesignVariant, ProjectKind, ProjectPreferences } from "./types";

type HealthResponse = { ok: boolean; mode: "demo" | "openai" };
type ProjectResponse = { id: string; status: string; expiresAt: string };
type StatusResponse = {
  projectStatus: "draft" | "queued" | "processing" | "completed" | "failed";
  job?: { status?: string; error_code?: string } | null;
};
type VariantRow = {
  id: string;
  project_id: string;
  title: string;
  explanation: string;
  image_url: string;
  sort_order: number;
  detected_json: string;
};

async function apiFetch(path: string, init?: RequestInit, timeoutMs = 5_000): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(path, {
      ...init,
      credentials: "include",
      headers: { Accept: "application/json", ...init?.headers },
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function detectApi(): Promise<HealthResponse | null> {
  try {
    const response = await apiFetch("/api/health", undefined, 1_500);
    if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return null;
    const health: HealthResponse = await response.json();
    return health.ok ? health : null;
  } catch {
    return null;
  }
}

export async function createApiProject(
  kind: ProjectKind,
  space: string,
  preferences: ProjectPreferences,
  imageUrl: string,
): Promise<ProjectResponse | null> {
  const health = await detectApi();
  if (!health) return null;

  const created = await apiFetch("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kind, space, ...preferences }),
  });
  if (!created.ok) throw new Error("Projekt nije moguće spremiti na poslužitelj.");
  const project: ProjectResponse = await created.json();

  const image = await fetch(imageUrl);
  if (!image.ok) throw new Error("Fotografiju nije moguće pripremiti za prijenos.");
  const blob = await image.blob();
  const uploaded = await apiFetch(
    `/api/projects/${project.id}/photo`,
    {
      method: "POST",
      headers: { "Content-Type": blob.type || "image/webp" },
      body: blob,
    },
    20_000,
  );
  if (!uploaded.ok) throw new Error("Fotografiju nije moguće sigurno prenijeti.");
  return project;
}

export async function startApiGeneration(projectId: string): Promise<void> {
  const response = await apiFetch(`/api/projects/${projectId}/generate`, { method: "POST" }, 10_000);
  if (!response.ok) throw new Error("Generiranje nije moguće pokrenuti.");
}

export async function getApiStatus(projectId: string): Promise<StatusResponse> {
  const response = await apiFetch(`/api/projects/${projectId}/status`, undefined, 10_000);
  if (!response.ok) throw new Error("Status projekta trenutačno nije dostupan.");
  return response.json();
}

export async function getApiVariants(projectId: string, kind: ProjectKind): Promise<DesignVariant[]> {
  const response = await apiFetch(`/api/projects/${projectId}/variants`, undefined, 10_000);
  if (!response.ok) throw new Error("Prijedloge nije moguće dohvatiti.");
  const rows: VariantRow[] = await response.json();
  const defaults = makeDemoVariants(projectId, kind);
  return rows.map((row, index) => {
    const fallback = defaults[index] ?? defaults[0];
    return {
      ...fallback,
      id: row.id,
      projectId: row.project_id,
      title: row.title,
      explanation: row.explanation,
      imageUrl: row.image_url,
      detectedElements: JSON.parse(row.detected_json) as DesignVariant["detectedElements"],
    };
  });
}

export async function deleteApiProject(projectId: string): Promise<void> {
  try {
    await apiFetch(`/api/projects/${projectId}`, { method: "DELETE" }, 10_000);
  } catch {
    // Local deletion remains immediate; the server retention job is the safety net.
  }
}
